import { createAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { setLocale } from 'react-redux-i18n';
import Auth from 'services/api/Auth';
import Impersonation from 'services/api/Impersonation';
import Login from 'services/api/Login';
import Http from 'services/Http';
import Socket from 'services/Socket';
import alert from 'store/app/ui/Alert/action';
import authCompany from 'store/selectors/authCompany';
import authCompanyId from 'store/selectors/authCompanyId';
import authUser from 'store/selectors/authUser';
import meStatus from 'store/selectors/meStatus';
import {
  clearTokenLastCompanyId,
  getActiveCompanyId,
  needsCompanyRefetch as computeNeedsCompanyRefetch,
  pickDefaultCompany,
  resolveCompanyFromSubdomain,
  setActiveCompanyId,
} from 'utils/activeCompany';
import getSubdomain from 'utils/getSubdomain';
import { parseMeResponse } from 'utils/mePayload';
import { clearImpersonationCookie } from 'utils/auth';
import { I18n } from 'utils/i18n';
import redirect from 'utils/redirect';
import { writeLocaleCookie } from 'utils/localeCookie';
import { getActiveAuthToken } from 'utils/getActiveAuthHeaders';
import shouldFetchMe from 'store/app/auth/shouldFetchMe';

export const SEND_AUTH_SMS = 'SEND_AUTH_SMS';
export const SEND_AUTH_SMS_PENDING = 'SEND_AUTH_SMS_PENDING';
export const SEND_AUTH_SMS_FULFILLED = 'SEND_AUTH_SMS_FULFILLED';
export const SEND_AUTH_SMS_REJECTED = 'SEND_AUTH_SMS_REJECTED';

export const SEND_AUTH_EMAIL = 'SEND_AUTH_EMAIL';
export const SEND_AUTH_EMAIL_PENDING = 'SEND_AUTH_EMAIL_PENDING';
export const SEND_AUTH_EMAIL_FULFILLED = 'SEND_AUTH_EMAIL_FULFILLED';
export const SEND_AUTH_EMAIL_REJECTED = 'SEND_AUTH_EMAIL_REJECTED';

export const GET_USER_DATA = 'GET_USER_DATA';
export const GET_USER_DATA_PENDING = 'GET_USER_DATA_PENDING';
export const GET_USER_DATA_FULFILLED = 'GET_USER_DATA_FULFILLED';
export const GET_USER_DATA_REJECTED = 'LOGIN_USER_REJECTED';

export const LOGIN_USER = 'LOGIN_USER';
export const LOGIN_USER_PENDING = 'LOGIN_USER_PENDING';
export const LOGIN_USER_FULFILLED = 'LOGIN_USER_FULFILLED';
export const LOGIN_USER_REJECTED = 'LOGIN_USER_REJECTED';

export const LOGOUT_USER = 'LOGOUT_USER';
export const LOGOUT_USER_PENDING = 'LOGOUT_USER_PENDING';

export const FLUSH_USER_DATA = 'FLUSH_USER_DATA';

export const SET_USER_PHONE = 'SET_USER_PHONE';
export const ADD_COMPANY = 'ADD_COMPANY';
export const REMOVE_COMPANY = 'REMOVE_COMPANY';
export const CHANGE_AUTH_EMPLOYEE = 'CHANGE_AUTH_EMPLOYEE';

export const ACCEPT_INVITATION = 'ACCEPT_INVITATION';

export const TOKEN_REFRESHED = 'TOKEN_REFRESHED';

export const VERIFY = 'VERIFY';
export const VERIFY_PENDING = 'VERIFY_PENDING';
export const VERIFY_FULFILLED = 'VERIFY_FULFILLED';
export const VERIFY_REJECTED = 'VERIFY_REJECTED';

export const LIST_SESSIONS = 'LIST_SESSIONS';
export const LIST_SESSIONS_PENDING = 'LIST_SESSIONS_PENDING';
export const LIST_SESSIONS_FULFILLED = 'LIST_SESSIONS_FULFILLED';
export const LIST_SESSIONS_REJECTED = 'LIST_SESSIONS_REJECTED';

export const DELETE_SESSIONS = 'DELETE_SESSIONS';
export const DELETE_SESSIONS_PENDING = 'DELETE_SESSIONS_PENDING';
export const DELETE_SESSIONS_FULFILLED = 'DELETE_SESSIONS_FULFILLED';
export const DELETE_SESSIONS_REJECTED = 'DELETE_SESSIONS_REJECTED';

export const DELETE_SESSION = 'DELETE_SESSION';
export const DELETE_SESSION_PENDING = 'DELETE_SESSION_PENDING';
export const DELETE_SESSION_FULFILLED = 'DELETE_SESSION_FULFILLED';
export const DELETE_SESSION_REJECTED = 'DELETE_SESSION_REJECTED';

export const START_IMPERSONATION_SUCCESS = 'START_IMPERSONATION_SUCCESS';
export const STOP_IMPERSONATION = 'STOP_IMPERSONATION';
export const IMPERSONATION_EXPIRED = 'IMPERSONATION_EXPIRED';

const getCompany = companies =>
  companies.find(c => c._id === getActiveCompanyId()) || { employee: { _id: 'x' } };

const user = () => dispatch => {
  const response = dispatch({
    type: GET_USER_DATA,
    payload: Auth.user(),
  });

  return response.then(({ action: { payload } }) => {
    const { user: meUser } = parseMeResponse(payload);
    const {
      employee: { settings },
    } = getCompany(meUser.companies);
    const language = settings?.language;
    if (language) {
      moment().locale(language);
      moment.locale(language);
      dispatch(setLocale(language));
      writeLocaleCookie(language);
    }
    return meUser;
  });
};

const login = (credentials, returnUrl, keepSessionOpen) => ({
  type: LOGIN_USER,
  payload: Login.login(credentials),
  meta: { returnUrl, keepSessionOpen },
});

const loginSuccess = ({
  user: u,
  token,
  keepSessionOpen,
  loginMethod,
  passkeyEnrollmentCompany,
  passkeyEnrollmentHandoff,
  returnUrl,
}) => ({
  type: LOGIN_USER_FULFILLED,
  payload: { token, user: u, loginMethod, passkeyEnrollmentCompany, passkeyEnrollmentHandoff },
  meta: { keepSessionOpen, returnUrl },
});

const tokenRefreshed = (token, keepSessionOpen) => ({
  type: TOKEN_REFRESHED,
  payload: { token, keepSessionOpen },
});

const logout = () => ({
  type: LOGOUT_USER,
  payload: Auth.logout(),
});

const flushUserData = createAction(FLUSH_USER_DATA);

const acceptInvite = (phone, token) => ({
  type: ACCEPT_INVITATION,
  payload: Auth.acceptInvite(phone, token),
});

const verify = () => ({
  type: VERIFY,
  payload: Auth.verify(),
});

const listSessions = userId => ({
  type: LIST_SESSIONS,
  payload: Auth.listSessions(userId),
});

const deleteSessions = userId => ({
  type: DELETE_SESSIONS,
  payload: Auth.deleteSessions(userId),
});

const deleteSession = (userId, sessionId) => ({
  type: DELETE_SESSION,
  payload: Auth.deleteSession(userId, sessionId),
});

const startImpersonationSuccess = payload => ({
  type: START_IMPERSONATION_SUCCESS,
  payload,
});

const impersonationExpired = () => ({
  type: IMPERSONATION_EXPIRED,
});

const completeImpersonationStart =
  ({ token, targetUser, expiresAt, resetCompany = false }) =>
  (dispatch, getState) => {
    const superadminUser = getState().auth.employee?.user || null;
    clearTokenLastCompanyId();
    dispatch(
      startImpersonationSuccess({
        token,
        targetUser,
        expiresAt,
        superadminUser,
      }),
    );
    Http.authorize();
    Socket.connect();
    dispatch(flushUserData());
    if (resetCompany) {
      const targetCompany = pickDefaultCompany(targetUser, getActiveCompanyId());
      if (targetCompany?._id) {
        setActiveCompanyId(targetCompany._id);
        if (targetCompany.subDomain) {
          redirect('/home/messages', targetCompany.subDomain);
        }
      }
    }
  };

const exitImpersonation =
  ({ reason = 'user', showNotice = true } = {}) =>
  async dispatch => {
    if (reason === 'user' || reason === 'proactive') {
      try {
        await Impersonation.stop();
      } catch {
        // Token may already be expired.
      }
    }
    dispatch(impersonationExpired());
    clearImpersonationCookie();
    Http.authorize();
    Socket.connect();
    if (showNotice) {
      dispatch(
        alert.show({
          message: I18n.t('Impersonation.Ended'),
          variant: 'info',
        }),
      );
    }
    dispatch(flushUserData());
    dispatch(user());
  };

// Coalesces concurrent /me fetches into one. useMe is consumed by several mounted components
// (AuthGate, HomeGate, WelcomeLayout); without this, each consumer (and each re-render before
// GET_USER_DATA_PENDING flips isFetching) schedules its own dispatch, firing /me 2-3x per switch.
let meFetchInFlight = false;

export const useMe = () => {
  const dispatch = useDispatch();
  const c = useSelector(authCompany);
  const companies = useSelector(state => state.companies);
  const me = useSelector(authUser);
  const storedCompanyId = useSelector(authCompanyId);
  const ret = useSelector(meStatus);
  const subdomain = getSubdomain();
  const persistedCompanies = Object.values(companies || {});
  const urlCompany =
    resolveCompanyFromSubdomain(persistedCompanies, subdomain) ||
    resolveCompanyFromSubdomain(me?.companies, subdomain);
  // Gate on the user's own membership only. company_id is resolved by the reducer from the user's
  // companies, so resolving the gate against the broader entity cache (which can hold companies the
  // user is not a member of, e.g. left over from a prior impersonation) would never converge and
  // would loop /me. When the URL subdomain is not one of the user's companies, HomeGate redirects.
  const userUrlCompanyId = resolveCompanyFromSubdomain(me?.companies, subdomain)?._id;
  const needsCompanyRefetch = computeNeedsCompanyRefetch({
    subdomain,
    urlCompanyId: userUrlCompanyId,
    storedCompanyId,
  });
  const impersonationTargetId = useSelector(state => state.auth.impersonation?.targetUser?._id);
  const impersonationStale = Boolean(
    impersonationTargetId && me?._id && String(me._id) !== String(impersonationTargetId),
  );
  const sessionToken = useSelector(state => getActiveAuthToken(state.auth));
  const shouldFetch = shouldFetchMe({
    sessionToken,
    ret,
    authCompany: c,
    subdomain,
    urlCompany,
    needsCompanyRefetch,
    impersonationStale,
  });
  if (shouldFetch && !meFetchInFlight) {
    meFetchInFlight = true;
    setTimeout(() => {
      Promise.resolve(dispatch(user()))
        .catch(() => {})
        .finally(() => {
          meFetchInFlight = false;
        });
    }, 1);
  }
  return { ...ret, needsCompanyRefetch };
};

export default {
  completeImpersonationStart,
  deleteSession,
  deleteSessions,
  exitImpersonation,
  flushUserData,
  impersonationExpired,
  user,
  listSessions,
  login,
  loginSuccess,
  logout,
  acceptInvite,
  startImpersonationSuccess,
  tokenRefreshed,
  verify,
};
